import { dmApi } from "../dmApi";
import { normalizeBundlesResponse, normalizeSingleBundleResponse, buildConfirmOrderRequest } from "../../helpers/vaInvoiceUtils";
import { normalizeVaMetricsResponse, buildVaMetricsQueryParams, normalizeAudiologistMetricsResponse, normalizeHospitalOrderVolumeResponse } from "../../helpers/vaMetricsUtils";
import {
  normalizeVaSettingsResponse,
  normalizeVaBillsResponse,
  toBillStatusParam,
  toBillTypeParam,
} from "../../helpers/vaSettingsUtils";

/** Prefer API `item` / `data`, else pass through. */
function unwrapData(response) {
  if (response == null) return response;
  if (response.item != null) return response.item;
  if (Object.prototype.hasOwnProperty.call(response, "data")) {
    return response.data;
  }
  return response;
}

/** Map live distributor fields onto the shape the UI already uses. */
function mapDistributor(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;

  const percent = item.commissionPercent ?? item.commission?.percent;
  const commission =
    item.commission ||
    (percent != null
      ? {
          id: item.commissionId || `comm-${percent}`,
          label: `${percent}%`,
          percent,
        }
      : undefined);

  const payment =
    item.paymentMethod && typeof item.paymentMethod === "object"
      ? item.paymentMethod
      : {};
  const cardNumber = payment.cardNumber ?? item.cardNumber ?? "";
  const routingNumber = payment.routingNumber ?? item.routingNumber ?? "";

  return {
    ...item,
    phone: item.phone ?? "",
    cardNumber,
    routingNumber,
    paymentMethod: {
      ...payment,
      cardNumber,
      routingNumber,
    },
    territories: Array.isArray(item.territories) ? item.territories : [],
    hospitalIds: Array.isArray(item.hospitalIds)
      ? item.hospitalIds.map(String)
      : [],
    ordersCount:
      item.assignedOrderCount ??
      item.ordersCount ??
      0,
    assignedOrderCount:
      item.assignedOrderCount ?? item.ordersCount ?? 0,
    cancelledOrderCount: Number(item.cancelledOrderCount) || 0,
    paidCommission: Number(item.paidCommission) || 0,
    pendingCommission: Number(item.pendingCommission) || 0,
    totalCommission: Number(item.totalCommission) || 0,
    totalAmount:
      item.totalAmount ??
      item.totalCommission ??
      0,
    commission,
  };
}

function normalizeDistributorList(response) {
  // List may be `{ success, items, pagination }` at the top level
  const itemsRaw =
    response?.items ??
    unwrapData(response)?.items ??
    unwrapData(response)?.distributors ??
    (Array.isArray(unwrapData(response)) ? unwrapData(response) : null) ??
    (Array.isArray(response) ? response : []);

  const items = (Array.isArray(itemsRaw) ? itemsRaw : []).map(mapDistributor);

  const pagination =
    response?.pagination ??
    unwrapData(response)?.pagination ?? {
      page: response?.page ?? 1,
      total: response?.total ?? items.length,
    };

  return {
    success: true,
    data: { items, pagination },
  };
}

function normalizeDistributor(response) {
  const payload = unwrapData(response);
  return { success: true, data: mapDistributor(payload) };
}

function normalizeOptionsList(response) {
  const payload = unwrapData(response);
  const list = Array.isArray(payload)
    ? payload
    : payload?.items ?? payload?.options ?? response?.items ?? [];
  return { success: true, data: list };
}

export const vaApi = dmApi.injectEndpoints({
  endpoints: (builder) => ({
    parseInvoice: builder.mutation({
      queryFn: async (file, _api, _extra, baseQuery) => {
        const formData = new FormData();
        formData.append("file", file);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 120000);
        try {
          const result = await baseQuery({
            url: "/va/invoices/parse",
            method: "POST",
            body: formData,
            signal: controller.signal,
          });
          if (result.error) {
            const status =
              result.error.status === "FETCH_ERROR" && controller.signal.aborted
                ? 502
                : result.error.status;
            return {
              error: {
                ...result.error,
                status,
                data: result.error.data || {
                  message: "Could not read invoice, try again",
                },
              },
            };
          }
          return { data: result.data };
        } finally {
          window.clearTimeout(timeoutId);
        }
      },
    }),

    parsePrescription: builder.mutation({
      queryFn: async (payload, _api, _extra, baseQuery) => {
        const file = payload?.file;
        const prescriptionFileUrl =
          payload?.prescriptionFileUrl ||
          payload?.prescriptionFileKey ||
          null;
        const formData = new FormData();
        if (file instanceof Blob) {
          formData.append("file", file);
        }
        if (payload?.parseId) {
          formData.append("parseId", String(payload.parseId));
        }
        if (payload?.orderedLensType) {
          formData.append("orderedLensType", String(payload.orderedLensType));
        }
        if (prescriptionFileUrl) {
          formData.append(
            "prescriptionFileUrl",
            String(prescriptionFileUrl)
          );
          formData.append(
            "prescriptionFileKey",
            String(prescriptionFileUrl)
          );
        }
        if (!(file instanceof Blob) && !prescriptionFileUrl) {
          return {
            error: {
              status: 400,
              data: {
                message: "A prescription PDF or image file is required.",
              },
            },
          };
        }
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 120000);
        try {
          const result = await baseQuery({
            url: "/va/invoices/parse-prescription",
            method: "POST",
            body: formData,
            signal: controller.signal,
          });
          if (result.error) {
            const status =
              result.error.status === "FETCH_ERROR" && controller.signal.aborted
                ? 502
                : result.error.status;
            return {
              error: {
                ...result.error,
                status,
                data: result.error.data || {
                  message: "Could not read prescription. Try again.",
                },
              },
            };
          }
          return { data: unwrapData(result.data) };
        } finally {
          window.clearTimeout(timeoutId);
        }
      },
    }),

    getVaDemoOrderOptions: builder.query({
      query: () => "/va/demo-orders/options",
      transformResponse: (response) => {
        const payload = unwrapData(response) || {};
        const options = Array.isArray(payload.options)
          ? payload.options
          : Array.isArray(payload)
            ? payload
            : [];
        return options
          .map((option) => ({
            code: String(option.code || "").trim().toUpperCase(),
            label: String(option.label || option.code || "").trim(),
            kind: option.kind === 'item' ? 'item' : 'bundle',
            category: option.category || 'va_bundles',
            available: option.available !== false,
          }))
          .filter((option) => option.code && option.label);
      },
    }),

    getVaDemoFacilities: builder.query({
      query: () => '/va/demo-orders/facilities',
      transformResponse: response => unwrapData(response)?.items || [],
    }),

    requestVaDemoOrder: builder.mutation({
      query: (body) => ({
        url: "/va/demo-orders",
        method: "POST",
        body,
      }),
      transformResponse: (response) => unwrapData(response) || response,
      invalidatesTags: ["getOrdersApi", "VaDistributorMetrics", "VaHospitalOrderVolume"],
    }),

    confirmVaOrder: builder.mutation({
      queryFn: async (payload, _api, _extra, baseQuery) => {
        // Audiologist Confirm and Pay
        if (payload.manual) {
          const result = await baseQuery({
            url: "/va/audiologist/orders/confirm",
            method: "POST",
            body: {
              bundleCode: payload.bundleCode,
              skuCode: payload.skuCode,
              delivery: payload.delivery,
              prescription: payload.prescriptionSkipped
                ? null
                : payload.prescription,
              prescriptions: payload.prescriptionSkipped
                ? null
                : payload.prescriptions || null,
              prescriptionSkipped: Boolean(payload.prescriptionSkipped),
              lensSelection: payload.lensSelection || null,
              paymentMethodId: payload.paymentMethodId,
              paymentIntentId: payload.paymentIntentId || null,
              prescriptionFileKey: payload.prescriptionFileKey || null,
            },
          });
          return result.error ? { error: result.error } : { data: result.data };
        }

        const result = await baseQuery({
          url: "/va/orders/confirm",
          method: "POST",
          body: buildConfirmOrderRequest(payload),
        });
        return result.error ? { error: result.error } : { data: result.data };
      },
    }),

    quoteAudiologistOrder: builder.mutation({
      query: (body) => ({
        url: "/va/audiologist/orders/quote",
        method: "POST",
        body,
      }),
      transformResponse: (response) => unwrapData(response) || response,
    }),

    getVaBundles: builder.query({
      query: () => "/va/bundles",
      transformResponse: (response) => ({
        success: true,
        data: normalizeBundlesResponse(response),
      }),
    }),

    /** Lightweight dropdown list: codes + labels only. */
    getVaBundleNames: builder.query({
      query: () => "/va/bundles/names",
      transformResponse: (response) => {
        let payload = response?.data ?? response;
        if (payload?.data && !payload?.bundles) {
          payload = payload.data;
        }
        const raw = payload?.bundles ?? (Array.isArray(payload) ? payload : []);
        return {
          success: true,
          data: (Array.isArray(raw) ? raw : [])
            .map((b) => ({
              code: String(b.code || "").trim().toUpperCase(),
              label: String(b.label || b.code || "").trim(),
              handle: b.handle || null,
            }))
            .filter((b) => {
              if (!b.code) return false;
              // Hide AUD1 / CAP9-TEST (any casing/separator) from create-order lists.
              const normalized = b.code.replace(/[^A-Z0-9]/gi, "").toUpperCase();
              return normalized !== "AUD1" && normalized !== "CAP9TEST";
            }),
        };
      },
    }),

    /** Fetch one Shopify bundle by code (lazy — used when admin changes selection). */
    getVaBundle: builder.query({
      query: (code) => `/va/bundles/${encodeURIComponent(String(code || "").trim())}`,
      transformResponse: (response) => ({
        success: true,
        data: normalizeSingleBundleResponse(response),
      }),
    }),

    // --- Distributor manage (live APIs only) ---

    getVaDistributor: builder.query({
      query: (id) => `/va/distributors/${id}`,
      transformResponse: normalizeDistributor,
      providesTags: (_result, _error, id) => [{ type: "VaDistributors", id }],
    }),

    getVaDistributors: builder.query({
      query: ({
        page = 1,
        limit = 20,
        search = "",
        period,
        from,
        to,
      } = {}) => ({
        url: "/va/distributors",
        params: {
          page,
          limit,
          sort: "createdAt",
          order: "desc",
          ...(search ? { search } : {}),
          ...(period ? { period } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        },
      }),
      transformResponse: normalizeDistributorList,
      providesTags: ["VaDistributors"],
    }),

    getVaDistributorsByState: builder.query({
      query: (state) => ({
        url: "/va/distributors",
        params: { state },
      }),
      transformResponse: (response) => {
        const payload = unwrapData(response);
        if (Array.isArray(payload)) {
          return { success: true, data: payload };
        }
        const items = payload?.items ?? [];
        // Map list rows to select options when API returns full distributor records
        if (items.length && items[0]?.territories) {
          return {
            success: true,
            data: items.map((d) => ({
              id: d.id,
              name: d.name,
              territory: Array.isArray(d.territories)
                ? d.territories.join(", ")
                : d.territory || "",
            })),
          };
        }
        return normalizeOptionsList(response);
      },
    }),

    getVaCommissions: builder.query({
      query: () => "/va/distributors/meta/commissions",
      transformResponse: (response) => {
        const raw =
          response?.items ??
          unwrapData(response)?.items ??
          (Array.isArray(unwrapData(response)) ? unwrapData(response) : []) ??
          [];
        const items = (Array.isArray(raw) ? raw : []).map((value) => {
          const percent = typeof value === "object" ? value.percent ?? value.id : value;
          return {
            id: String(percent),
            value: percent,
            percent: Number(percent),
            label: `${percent}%`,
          };
        });
        return { success: true, data: items };
      },
    }),

    getVaHospitals: builder.query({
      query: () => "/va/distributors/meta/hospitals",
      providesTags: ["VaHospitals"],
      transformResponse: (response) => {
        const raw =
          response?.items ??
          unwrapData(response)?.items ??
          (Array.isArray(unwrapData(response)) ? unwrapData(response) : []) ??
          [];
        const items = (Array.isArray(raw) ? raw : []).map((group) => ({
          stateCode: String(group.stateCode || "").toUpperCase(),
          stateName: group.stateName || group.stateCode || "",
          region: group.region || "",
          hospitals: (Array.isArray(group.hospitals) ? group.hospitals : []).map(
            (hospital) => ({
              id: String(hospital.id),
              name: hospital.name || "",
              city: hospital.city || "",
              slug: hospital.slug || "",
              assignedDistributorId: hospital.assignedDistributorId
                ? String(hospital.assignedDistributorId)
                : null,
              assignedDistributorName: hospital.assignedDistributorName || null,
            })
          ),
        }));
        return { success: true, data: items };
      },
    }),

    getVaAuth0Users: builder.query({
      query: ({ page = 1, limit = 50, search = "", role = "va-distributor" } = {}) => ({
        url: "/va/distributors/auth0/users",
        params: {
          page,
          limit,
          role,
          ...(search ? { search } : {}),
        },
      }),
      transformResponse: (response) => {
        const payload = unwrapData(response);
        const raw =
          response?.items ??
          response?.users ??
          payload?.items ??
          payload?.users ??
          (Array.isArray(payload) ? payload : []) ??
          [];

        const items = (Array.isArray(raw) ? raw : []).map((user) => {
          const auth0UserId =
            user.user_id ||
            user.userId ||
            user.auth0UserId ||
            user.id ||
            "";
          const name =
            user.name ||
            user.nickname ||
            user.email ||
            auth0UserId;
          return {
            id: String(auth0UserId || name),
            auth0UserId: String(auth0UserId),
            name,
            email: user.email || "",
          };
        });

        return { success: true, data: items };
      },
    }),

    getVaDistributorCandidates: builder.query({
      query: () => "/va/distributors/candidates",
      transformResponse: normalizeOptionsList,
    }),

    createVaDistributor: builder.mutation({
      query: (body) => ({
        url: "/va/distributors",
        method: "POST",
        body,
      }),
      transformResponse: normalizeDistributor,
      invalidatesTags: ["VaDistributors", "VaHospitals"],
    }),

    updateVaDistributor: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/va/distributors/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: normalizeDistributor,
      invalidatesTags: (_result, _error, { id }) => [
        "VaDistributors",
        "VaHospitals",
        { type: "VaDistributors", id },
      ],
    }),

    removeVaDistributor: builder.mutation({
      query: (arg) => {
        const distributorIds = Array.isArray(arg)
          ? arg
          : Array.isArray(arg?.distributorIds)
            ? arg.distributorIds
            : arg?.id
              ? [arg.id]
              : arg
                ? [arg]
                : [];
        return {
          url: "/va/distributors/remove",
          method: "POST",
          body: { distributorIds },
        };
      },
      invalidatesTags: ["VaDistributors", "VaHospitals"],
    }),

    getVaDistributorMetrics: builder.query({
      queryFn: async (filter = {}, _api, _extra, baseQuery) => {
        const params = buildVaMetricsQueryParams(filter);
        const result = await baseQuery({
          url: "/va/metrics",
          params,
        });
        if (result.error) return { error: result.error };
        return { data: normalizeVaMetricsResponse(result.data) };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify(buildVaMetricsQueryParams(queryArgs)),
      providesTags: ["VaDistributorMetrics"],
    }),

    getVaHospitalOrderVolume: builder.query({
      queryFn: async (filter = { period: "last_month" }, _api, _extra, baseQuery) => {
        const params = buildVaMetricsQueryParams(filter);
        const result = await baseQuery({
          url: "/va/metrics/hospitals",
          params,
        });
        if (result.error) return { error: result.error };
        return { data: normalizeHospitalOrderVolumeResponse(result.data) };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify(["hospitals", buildVaMetricsQueryParams(queryArgs)]),
      providesTags: ["VaHospitalOrderVolume"],
    }),

    getVaHospitalsWithOrders: builder.query({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: "/va/metrics/hospitals-with-orders",
        });
        if (result.error) return { error: result.error };

        const payload = result.data;
        const unwrapped =
          payload?.data?.hospitals != null
            ? payload.data
            : payload?.hospitals != null
              ? payload
              : unwrapData(payload);

        const hospitals = Array.isArray(unwrapped?.hospitals)
          ? unwrapped.hospitals
          : Array.isArray(unwrapped)
            ? unwrapped
            : [];

        return {
          data: hospitals
            .map((h) => ({
              id: String(h.id || h._id || "").trim(),
              name: String(h.name || "").trim() || "Unknown hospital",
              orderCount: Number(h.orderCount) || 0,
            }))
            .filter((h) => h.id),
        };
      },
      providesTags: ["VaHospitalsWithOrders"],
    }),

    getVaAudiologistMetrics: builder.query({
      queryFn: async (filter = { period: "this_month" }, _api, _extra, baseQuery) => {
        const params = buildVaMetricsQueryParams(filter);
        const result = await baseQuery({
          url: "/va/audiologist/metrics",
          params,
        });
        if (result.error) return { error: result.error };
        return { data: normalizeAudiologistMetricsResponse(result.data) };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify(buildVaMetricsQueryParams(queryArgs)),
    }),

    getVaAudiologistSkus: builder.query({
      query: () => "/va/audiologist/skus",
      transformResponse: (response) => {
        const data = unwrapData(response);
        const skus = Array.isArray(data?.skus)
          ? data.skus
          : Array.isArray(data)
            ? data
            : [];
        return skus.map((sku) => ({
          code: String(sku.code || sku.productId || "").trim(),
          productId: sku.productId ? String(sku.productId) : null,
          label: String(sku.label || sku.title || "").trim(),
          price: sku.price != null ? Number(sku.price) : null,
          imageUrl: sku.imageUrl || null,
          imageAlt: sku.imageAlt || sku.label || "",
          handle: sku.handle || null,
          variantId: sku.variantId || null,
          sku: sku.sku || null,
        }));
      },
      providesTags: ["VaAudiologistSkus"],
    }),

    getVaAudiologistBundles: builder.query({
      query: (productId) => ({
        url: "/va/audiologist/bundles",
        params: productId ? { productId: String(productId) } : undefined,
      }),
      transformResponse: (response) => {
        const data = unwrapData(response);
        const bundles = Array.isArray(data?.bundles)
          ? data.bundles
          : Array.isArray(data)
            ? data
            : [];
        return bundles.map((bundle) => {
          const lenses = bundle.lenses || "";
          const lensesItems = Array.isArray(bundle.lensesItems)
            ? bundle.lensesItems.map((item) => String(item || "").trim()).filter(Boolean)
            : [];
          const lensesText = String(lenses).trim().toLowerCase();
          const hasLensesText =
            lensesItems.length > 0 ||
            (Boolean(lensesText) &&
              !["none", "n/a", "—", "-", "null", "undefined"].includes(
                lensesText
              ));

          return {
            code: String(bundle.code || "").trim().toUpperCase(),
            label: String(bundle.label || bundle.code || "").trim(),
            productId: bundle.productId ? String(bundle.productId) : null,
            includesLenses:
              Boolean(bundle.includesLenses) || hasLensesText,
            items: Array.isArray(bundle.items) ? bundle.items : [],
            glasses: bundle.glasses || "",
            glassesItems: Array.isArray(bundle.glassesItems)
              ? bundle.glassesItems
              : [],
            accessories: bundle.accessories || "",
            accessoriesItems: Array.isArray(bundle.accessoriesItems)
              ? bundle.accessoriesItems
              : [],
            handheld: bundle.handheld || "",
            handheldItems: Array.isArray(bundle.handheldItems)
              ? bundle.handheldItems
              : [],
            lenses,
            lensesItems,
            itemCount: Number(bundle.itemCount) || 0,
          };
        });
      },
      providesTags: ["VaAudiologistBundles"],
    }),

    getVaSettings: builder.query({
      query: () => "/va/settings",
      transformResponse: normalizeVaSettingsResponse,
      providesTags: ["VaSettings"],
    }),

    updateVaPaymentMethod: builder.mutation({
      query: (body) => {
        const accountHolderName = String(body.accountHolderName || "").trim();

        return {
          url: "/va/settings/payment-method",
          method: "PUT",
          body: {
            accountHolderName,
            routingNumber: body.routingNumber,
            accountNumber: body.accountNumber,
            accountType: body.accountType || "checking",
          },
        };
      },
      transformResponse: normalizeVaSettingsResponse,
      invalidatesTags: ["VaSettings"],
    }),

    changeVaPassword: builder.mutation({
      query: (body) => ({
        url: "/va/settings/password",
        method: "PUT",
        body: {
          password: body.password,
          confirmPassword: body.confirmPassword,
        },
      }),
    }),

    getVaBills: builder.query({
      query: ({ status = "all", type = "all", page = 1, limit = 10 } = {}) => ({
        url: "/va/bills",
        params: {
          status: toBillStatusParam(status),
          type: toBillTypeParam(type),
          page,
          limit,
        },
      }),
      transformResponse: normalizeVaBillsResponse,
      providesTags: ["VaBills"],
    }),

    getVaPayoutLogActions: builder.query({
      query: ({
        distributorId,
        page = 1,
        limit = 10,
        history = true,
        status,
      } = {}) => ({
        url: "/va/payout-logs/actions",
        params: {
          page,
          limit,
          ...(distributorId ? { distributorId } : {}),
          ...(history ? { history: true } : {}),
          ...(status && status !== "all" ? { status } : {}),
        },
      }),
      transformResponse: (response) => {
        const payload = unwrapData(response) || {};
        const items = Array.isArray(payload.items) ? payload.items : [];
        const pagination = payload.pagination || {
          page: 1,
          limit: items.length,
          total: items.length,
          totalPages: items.length ? 1 : 0,
        };
        return { success: true, data: { items, pagination } };
      },
    }),
  }),
});

export const {
  useParseInvoiceMutation,
  useParsePrescriptionMutation,
  useGetVaDemoOrderOptionsQuery,
  useGetVaDemoFacilitiesQuery,
  useRequestVaDemoOrderMutation,
  useConfirmVaOrderMutation,
  useQuoteAudiologistOrderMutation,
  useGetVaBundlesQuery,
  useGetVaBundleNamesQuery,
  useGetVaBundleQuery,
  useLazyGetVaBundleQuery,
  useGetVaDistributorsQuery,
  useGetVaDistributorQuery,
  useGetVaDistributorsByStateQuery,
  useGetVaCommissionsQuery,
  useGetVaHospitalsQuery,
  useGetVaAuth0UsersQuery,
  useGetVaDistributorCandidatesQuery,
  useCreateVaDistributorMutation,
  useUpdateVaDistributorMutation,
  useRemoveVaDistributorMutation,
  useGetVaDistributorMetricsQuery,
  useGetVaHospitalOrderVolumeQuery,
  useGetVaHospitalsWithOrdersQuery,
  useGetVaAudiologistMetricsQuery,
  useGetVaAudiologistSkusQuery,
  useGetVaAudiologistBundlesQuery,
  useGetVaSettingsQuery,
  useUpdateVaPaymentMethodMutation,
  useChangeVaPasswordMutation,
  useGetVaBillsQuery,
  useGetVaPayoutLogActionsQuery,
} = vaApi;
